import { ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import ModalShell from './ModalShell'

type Props = {
  isOpen: boolean
  title: string
  description?: string
  maxWidthClassName?: string
  onClose: () => void
  children: ReactNode
  /**
   * Default: bottom sheet on narrow viewports (full-width), centered from `sm` up.
   * When true: centered dialog with padding on all sizes — use for dense or tall content on phones.
   */
  mobileCentered?: boolean
}

export default function Modal({
  isOpen,
  title,
  description,
  maxWidthClassName = 'max-w-md',
  onClose,
  children,
  mobileCentered = false,
}: Props) {
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return
    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
    }
  }, [isOpen])

  // Track where mousedown started so we only close when both
  // mousedown AND mouseup happen on the backdrop (not dragged from inside)
  const mouseDownTarget = useRef<EventTarget | null>(null)

  if (!isOpen) return null

  const backdropLayout = mobileCentered
    ? 'items-center justify-center p-4'
    : 'items-end justify-center p-0 sm:items-center sm:p-4'

  const modal = (
    <div
      className={`modal-backdrop fixed inset-0 z-50 flex ${backdropLayout}`}
      onMouseDown={(e) => {
        mouseDownTarget.current = e.target
      }}
      onMouseUp={(e) => {
        if (e.target === e.currentTarget && mouseDownTarget.current === e.currentTarget) {
          onClose()
        }
        mouseDownTarget.current = null
      }}
      role="dialog"
      aria-modal="true"
    >
      <ModalShell
        className={`w-full min-h-0 ${maxWidthClassName} max-h-[min(95vh,100dvh)] sm:max-h-[min(90vh,100dvh)] flex flex-col overflow-hidden`}
      >
        <div className="modal-header p-4 sm:p-6 flex items-start justify-between gap-4 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="modal-title text-lg font-semibold truncate">{title}</h2>
            {description ? <p className="modal-description text-sm mt-1">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close flex-shrink-0"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        <div className="px-4 pb-4 pt-0 sm:px-6 sm:pb-6 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          {children}
        </div>
      </ModalShell>
    </div>
  )

  return typeof document === 'undefined' ? null : createPortal(modal, document.body)
}

